import { AntdConfig, Config } from '@react-awesome-query-builder/antd';
import { SuggestionTypeEnum } from '../../utils/tiptap/tiptapInterfaces';
import { transform } from './queryBuilderUtils';
import {
  VitalLabMatcherWidget,
  NumericValueWithUnitWidget,
  ValueComparisonWidget,
  MedicationNameParserWidget,
} from './PhenotypeWidgets';

// TODO: Triggers created using the phenotype trigger as a field are currently not working.
export const ehrConfig: Partial<Config> = {
  fields: {
    fhir: {
      label: 'FHIR',
      type: '!struct',
      subfields: {
        labs: {
          conjunctions: ['AND'],
          showNot: true,
          fieldName: SuggestionTypeEnum.LAB_DATA,
          label: 'Labs',
          mode: 'array',
          type: '!group',
          operators: ['with'],
          //valueSources: ['value'],
          initialEmptyWhere: true,
          subfields: {
            lookBackPeriodDays: {
              label: 'Look-back Period (Days)',
              type: 'number',
              valueSources: ['value'],
              operators: ['equal'],
            },
            valueComparison: {
              label: 'Value Comparison',
              type: 'value_comparison',
              valueSources: ['value'],
            },
            forLastLabDraws: {
              label: 'For Last # Lab Draws',
              type: 'number',
              valueSources: ['value'],
              operators: ['equal'],
            },
            trend: {
              label: 'Trend',
              type: 'select',
              operators: ['select_equals'],
              valueSources: ['value'],
              fieldSettings: {
                listValues: [
                  { value: 'up_trending', title: 'Uptrending' },
                  { value: 'down_trending', title: 'Downtrending' },
                  { value: 'un_changed', title: 'Unchanged' },
                ],
              },
            },
            multipleLabSeparation: {
              label: 'Multiple Lab Separation',
              type: 'numeric_value_with_unit',
              valueSources: ['value'],
              mainWidgetProps: {
                listValues: [
                  { value: 'mins', title: 'Minutes' },
                  { value: 'hours', title: 'Hours' },
                  { value: 'days', title: 'Days' },
                ],
              },
            },
          },
        } as any, // There is no interface definition for `fieldName: SuggestionTypeEnum.LAB_DATA`, but the functionality works.
        medications: {
          conjunctions: ['AND'],
          showNot: true,
          fieldName: SuggestionTypeEnum.MEDICATION,
          label: 'Medications',
          mode: 'array',
          type: '!group',
          operators: ['with'],
          //valueSources: ['value'],
          initialEmptyWhere: true,
          subfields: {
            medicationStatus: {
              label: 'Medication Status',
              type: 'select',
              operators: ['select_equals'],
              valueSources: ['value'],
              fieldSettings: {
                listValues: [
                  { value: 'active', title: 'Active Medication List' },
                  { value: 'inactive', title: 'Inactive Medication List' },
                ],
              },
            },
            medicationDose: {
              label: 'Medication Dose',
              type: 'numeric_value_with_unit',
              valueSources: ['value'],
              mainWidgetProps: {
                listValues: [
                  { value: 'mcg', title: 'mcg' },
                  { value: 'mg', title: 'mg' },
                  { value: 'g', title: 'g' },
                  { value: 'meq_per_l', title: 'mEq/L' },
                  { value: 'mg_per_dl', title: 'mg/dL' },
                ],
              },
            },
            lookBackPeriodDays: {
              label: 'Look-back Period (Days)',
              type: 'number',
              valueSources: ['value'],
              operators: ['equal'],
            },
            doseChangeHistory: {
              label: 'Dose Change History',
              type: 'number',
              valueSources: ['value'],
              operators: [
                'equal',
                'not_equal',
                'less',
                'less_or_equal',
                'greater',
                'greater_or_equal',
              ],
            },
            frequency: {
              label: 'Frequency',
              type: 'numeric_value_with_unit',
              valueSources: ['value'],
              mainWidgetProps: {
                listValues: [
                  { value: 'weekly', title: 'weekly' },
                  { value: 'daily', title: 'daily' },
                ],
              },
            },
            route: {
              label: 'Route',
              type: 'select',
              operators: ['select_equals'],
              valueSources: ['value'],
              fieldSettings: {
                listValues: [
                  { value: 'po', title: 'PO' },
                  { value: 'iv', title: 'IV' },
                  { value: 'im', title: 'IM' },
                  { value: 'inhalation', title: 'Inhalation' },
                  { value: 'topical', title: 'Topical' },
                ],
              },
            },
          },
        } as any, // There is no interface definition for `fieldName: SuggestionTypeEnum.MEDICATION`, but the functionality works.
      },
    },
  },
  types: {
    value_comparison: {
      valueSources: ['value'],
      defaultOperator: 'equal',
      widgets: {
        valueComparison: {
          operators: ['equal', 'not_equal', 'less', 'less_or_equal', 'greater', 'greater_or_equal'],
        },
      },
      mainWidget: 'valueComparison',
    },
    numeric_value_with_unit: {
      valueSources: ['value'],
      defaultOperator: 'equal',
      widgets: {
        numericValueWithUnit: {
          operators: ['equal', 'not_equal', 'less', 'less_or_equal', 'greater', 'greater_or_equal'],
        },
      },
      mainWidget: 'numericValueWithUnit',
    },
    '!group': {
      defaultOperator: 'with',
      mainWidget: 'clinicalDataNameMatcher',
      widgets: {
        clinicalDataNameMatcher: {
          operators: ['with'],
        } as any, // Due to the custom operator 'with', there are properties not defined in the base interface, so it's handled with 'any'
      },
    },
  },
  widgets: {
    valueComparison: {
      type: 'value_comparison',
      factory: (props) => <ValueComparisonWidget {...props} />,
      valuePlaceholder: 'Enter value',
      formatValue: (val) => Object.values(val).join(','),
    },
    numericValueWithUnit: {
      type: 'numeric_value_with_unit',
      factory: (props) => <NumericValueWithUnitWidget {...props} />,
      valuePlaceholder: 'Enter value',
      formatValue: (val) => Object.values(val).join(','),
    },
    clinicalDataNameMatcher: {
      type: 'with',
      factory: (props) => {
        switch (props.fieldDefinition.fieldName) {
          case SuggestionTypeEnum.LAB_DATA:
            return <VitalLabMatcherWidget {...props} />;
          case SuggestionTypeEnum.MEDICATION:
          default:
            return <MedicationNameParserWidget {...props} />;
        }
      },
    },
  },
  operators: {
    with: {
      label: 'with',
      labelForFormat: 'with',
      cardinality: 1,
      valueSources: ['value'],
      // Phenotype logic is stored inside 'with' and converted for use as jsonLogic
      // The 'field' represents logic in the form of basic group operators like reduce, filter, or accumulator, which is then converted into the 'with' structure and its sub-logic
      // In the sub-logic, data items contain the operator, and the operator holds values like ('==', '!='), which need to be switched accordingly through the `transform` function
      jsonLogic: (field, op, vals, opDef, operatorOptions, fieldDef) => {
        if (
          field &&
          typeof field === 'object' &&
          'reduce' in field &&
          Array.isArray(field.reduce) &&
          field.reduce.length > 0
        ) {
          // In the webapp, each EHR data item stored in variables (e.g., variables.lab_data, variables.vital_sign, etc.) is saved in json-logic as { var: lab_data } format.
          // However, for labs, both lab_data and vital_sign must be attached separately at the end of the json-logic elements.
          const type = fieldDef?.fieldName;
          const targetTypes =
            type === SuggestionTypeEnum.LAB_DATA
              ? [SuggestionTypeEnum.LAB_DATA, SuggestionTypeEnum.VITAL_SIGN]
              : [type];
          const variables = targetTypes.map((t) => ({
            var: `variables.${t}`,
          }));
          // Although 'type' has no functional effect, it was added because json-logic interprets a single-element 'vals' array as an operation
          const withPart = [{ ...vals, type }, { var: 'acc' }, ...variables];
          const filterPart = field.reduce[0].filter?.[1];
          const transformedFilterPart = transform(filterPart);

          return {
            and: [
              {
                with: withPart,
              },
              ...(transformedFilterPart !== undefined ? [transformedFilterPart] : []),
            ],
          };
        }
      },
    },
  },
  settings: {
    addSubRuleLabel: '',
    // Hide the operator input when selecting a phenotype field; subfields are not applicable
    renderOperator: (props, extra) => {
      // Other EHR types may be added in the future
      if (
        [SuggestionTypeEnum.LAB_DATA, SuggestionTypeEnum.MEDICATION].includes(
          props.fieldConfig.fieldName
        )
      )
        return null;
      const ro = AntdConfig.settings.renderOperator;
      return typeof ro === 'function' ? ro(props, extra) : <div className='display-none' />;
    },
    // Change the string format of phenotype to 'field WHERE condition with value;'
    // Since it's used to distinguish between positiveTrigger and conditionLogic in the web app, synchronization is required when modified
    formatAggr: (...args) => {
      const [whereStr, field, operator, value, , , { labelForFormat }] = args;
      // Other EHR types may be added in the future
      if (
        [SuggestionTypeEnum.LAB_DATA, SuggestionTypeEnum.MEDICATION].includes(field) &&
        operator === 'with'
      ) {
        // In the web app, the condition string is not used to apply conditionLogic without modifying the existing positiveTrigger logic
        // Use the commented section below if needed
        // const cond = whereStr ? ` WHERE ${whereStr}` : '';
        // return `${field}${cond} ${labelForFormat} ${value};`;
        return undefined;
      }
      const fa = AntdConfig.settings.formatAggr;
      if (typeof fa === 'function') {
        return (fa as (...args: any[]) => any)(...args);
      }
      return undefined;
    },
  } as any, // Handle with 'any' because some settings are used without being explicitly defined
};
